import { Link } from 'react-router-dom'

import { api, unwrap } from '../api/client'
import { useSealedQuery } from '../api/sealed'
import { useSessionEra } from '../documents/held'
import { t } from '../i18n'
import { ErrorNotice, Pending, formatDate } from '../ui'
import { Photograph } from './Photograph'

/** How many photographs the card shows. */
export const RECENT_SHOWN = 4

/**
 * The newest photographs on the project, small, linking to the Photos screen.
 *
 * The photo register's own list, newest first by when the picture was taken,
 * stored rows only. A row with no thumbnail on this box is skipped rather than
 * drawn as a broken tile, which is why a few more than four are asked for.
 * Nothing about where a photo was taken is shown here.
 */
export function RecentPhotosCard({ projectId }: { projectId: string }) {
  // Read under this session and sealed on a refusal, so a refused read takes
  // the photographs and captions away rather than leaving them up beside it.
  const era = useSessionEra()
  const { query: read, data } = useSealedQuery({
    key: ['photos', 'recent', projectId],
    era,
    scope: `photos/recent/${projectId}`,
    retry: false,
    read: async () =>
      unwrap(
        await api.GET('/photos/media', {
          params: {
            query: {
              project_id: projectId,
              limit: RECENT_SHOWN * 2,
              state: 'stored',
            },
          },
        }),
      ),
  })

  const rows = (data ?? []).filter((row) => row.has_thumbnail).slice(0, RECENT_SHOWN)
  const screen = `/p/${projectId}/photos`

  return (
    <section className="card" aria-labelledby="recent-photos-heading">
      <div className="row">
        <h3 id="recent-photos-heading" style={{ flex: 1 }}>
          {t('home.photos.title')}
        </h3>
        <Link to={screen}>{t('home.photos.all')}</Link>
      </div>
      {read.isPending ? <Pending /> : null}
      {read.error ? <ErrorNotice error={read.error} retry={() => void read.refetch()} /> : null}
      {data && rows.length === 0 ? <p className="muted">{t('home.photos.empty')}</p> : null}
      <ul className="recent-photos">
        {rows.map((row) => (
          <li key={row.id}>
            <Link to={screen}>
              <Photograph
                mediaId={row.id}
                caption={row.caption}
                version={row.thumbnail_version}
              />
              <span className="when">{formatDate(row.captured_on)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
